import { useEffect, useRef, useState } from 'react'

// Dependency-free SVG star (radar) chart for ONE series over a handful of categories —
// the sibling of TrendLine. One series → one hue: a 2px outline over a faint area, a
// point on every spoke. Each spoke's end carries the category's own colour as a small
// dot (identity); every label and number stays in text ink, never the series colour.
//   axes     [{ key, label, value, color }] — 3 or more; drawn clockwise from the top
//   color    the series hue
//   unit     after each number ("ราย")
//   onPick   (key) => void — click a point or a label (filters the list below)
//   activeKey  the picked category, drawn emphasised
// Hover (or focus) a point: a tooltip with the value and its share of the whole.

const fmt = (n) => Math.round(Number(n) || 0).toLocaleString('en-US')

// ring steps for small counts: 0 … rMax in steps of 1 · 2 · 5 · 10 …
function ringScale(peak) {
  if (peak <= 0) return { rMax: 4, step: 1 }
  const raw = peak / 4
  const pow = Math.pow(10, Math.floor(Math.log10(raw)))
  const step = Math.max(1, [1, 2, 5, 10].map((m) => m * pow).find((s) => s >= raw) || 10 * pow)
  return { rMax: Math.ceil(peak / step) * step, step }
}

export default function StarChart({ axes = [], color = '#2563eb', unit = '', height = 260, emptyText = 'ไม่มีข้อมูล', onPick, activeKey }) {
  const wrapRef = useRef(null)
  const [width, setWidth] = useState(360)
  const [hover, setHover] = useState(null)
  const values = axes.map((a) => Number(a.value) || 0)
  const total = values.reduce((s, v) => s + v, 0)
  const empty = axes.length < 3 || total === 0

  // re-attach when the box swaps between the empty note and the chart
  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const fit = (w) => { if (w) setWidth(Math.max(260, Math.round(w))) }
    fit(el.getBoundingClientRect().width)
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((entries) => fit(entries[0]?.contentRect?.width))
    ro.observe(el)
    return () => ro.disconnect()
  }, [empty])

  if (empty) {
    return <div ref={wrapRef} className="flex items-center justify-center text-[13px] text-slate-400" style={{ height }}>{emptyText}</div>
  }

  const n = axes.length
  const cx = width / 2
  const cy = height / 2 + 4
  // leave room left/right for the labels and top/bottom for two lines of text
  const R = Math.max(40, Math.min(width / 2 - 92, height / 2 - 40))
  const { rMax, step } = ringScale(Math.max(...values))
  const rings = Array.from({ length: Math.round(rMax / step) }, (_, i) => (i + 1) * step)
  const ang = (i) => -Math.PI / 2 + (2 * Math.PI * i) / n
  const at = (i, r) => [cx + r * Math.cos(ang(i)), cy + r * Math.sin(ang(i))]
  const poly = (r) => axes.map((_, i) => at(i, r).join(',')).join(' ')
  const pts = values.map((v, i) => at(i, (R * v) / rMax))
  const pick = (k) => onPick?.(k)
  const hv = hover != null ? axes[hover] : null

  return (
    <div ref={wrapRef} className="relative w-full">
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} className="select-none" role="img"
        aria-label={axes.map((a, i) => `${a.label} ${fmt(values[i])} ${unit}`).join(' · ')}>
        {/* rings and spokes — recessive */}
        {rings.map((t) => <polygon key={t} points={poly((R * t) / rMax)} fill="none" stroke="#eef2f6" strokeWidth={1} />)}
        {axes.map((a, i) => {
          const [x, y] = at(i, R)
          return <line key={a.key} x1={cx} y1={cy} x2={x} y2={y} stroke="#e2e8f0" strokeWidth={1} />
        })}
        {/* the ring values, up the top spoke */}
        {rings.map((t) => (
          <text key={`t${t}`} x={cx + 4} y={cy - (R * t) / rMax} dy="0.32em" className="fill-slate-400" fontSize={10}>{t}</text>
        ))}

        {/* the series */}
        <polygon points={pts.map((p) => p.join(',')).join(' ')} fill={color} fillOpacity={0.12} stroke={color} strokeWidth={2} strokeLinejoin="round" />

        {axes.map((a, i) => {
          const [ex, ey] = at(i, R)
          const [lx, ly] = at(i, R + 14)
          const cos = Math.cos(ang(i))
          const anchor = cos > 0.3 ? 'start' : cos < -0.3 ? 'end' : 'middle'
          const sin = Math.sin(ang(i))
          // labels above the top spoke sit higher; below the bottom ones, lower
          const y0 = sin < -0.3 ? ly - 14 : sin > 0.3 ? ly + 6 : ly - 4
          const on = activeKey != null && String(activeKey) === String(a.key)
          const [px, py] = pts[i]
          return (
            <g key={a.key} className={onPick ? 'cursor-pointer' : ''} onClick={() => pick(a.key)}
              onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(i)} onBlur={() => setHover(null)}
              tabIndex={onPick ? 0 : undefined} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(a.key) } }}>
              <circle cx={ex} cy={ey} r={3} fill={a.color || '#94a3b8'} />
              <text x={lx} y={y0} textAnchor={anchor} fontSize={11.5}
                className={on ? 'fill-brand-700' : 'fill-slate-600'} fontWeight={on ? 600 : 400}>{a.label}</text>
              <text x={lx} y={y0 + 15} textAnchor={anchor} fontSize={12.5} className="fill-slate-800" fontWeight={600}>
                {fmt(values[i])}{unit && <tspan className="fill-slate-400" fontWeight={400} fontSize={11}> {unit}</tspan>}
              </text>
              {/* the point, and a hit area bigger than it */}
              <circle cx={px} cy={py} r={on || hover === i ? 5 : 4} fill={color} stroke="#fff" strokeWidth={2} />
              <circle cx={px} cy={py} r={14} fill="transparent" />
            </g>
          )
        })}
      </svg>

      {hv && (
        <div className="pointer-events-none absolute left-1/2 top-1 z-10 -translate-x-1/2 whitespace-nowrap rounded-lg bg-slate-900/95 px-3 py-1.5 text-[11.5px] text-white shadow-lg">
          <span className="mr-1.5 inline-block h-2 w-2 rounded-full align-middle" style={{ background: hv.color || color }} />
          {hv.label} <b className="tabular-nums">{fmt(hv.value)}</b>{unit ? ` ${unit}` : ''}
          <span className="text-slate-300"> · {total ? Math.round(((Number(hv.value) || 0) / total) * 100) : 0}% ของทั้งหมด</span>
        </div>
      )}
    </div>
  )
}
